import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  Link,
  Snackbar,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ScheduleIcon from "@mui/icons-material/Schedule";
import axios from "axios";

function WatchlistDetail({ watchlistId, onBack }) {
  const navigate = useNavigate();
  const [watchlist, setWatchlist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newTicker, setNewTicker] = useState("");
  const [adding, setAdding] = useState(false);
  const [analyzingTicker, setAnalyzingTicker] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [updatingSchedule, setUpdatingSchedule] = useState(false);

  const fetchWatchlist = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`/api/watchlists/${watchlistId}`);
      if (response.data.success) {
        setWatchlist(response.data.data);
      } else {
        setError(response.data.error || "Failed to load watchlist");
      }
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load watchlist");
    } finally {
      setLoading(false);
    }
  }, [watchlistId]);

  useEffect(() => {
    fetchWatchlist();
  }, [fetchWatchlist]);

  const handleAddTicker = async () => {
    const ticker = newTicker.trim().toUpperCase();
    if (!ticker) return;
    setAdding(true);
    setError(null);
    try {
      const response = await axios.post(`/api/watchlists/${watchlistId}/items`, { ticker });
      if (response.data.success) {
        setNewTicker("");
        fetchWatchlist();
      } else {
        setError(response.data.error || "Failed to add ticker");
      }
    } catch (err) {
      setError(err.response?.data?.error || "Failed to add ticker");
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveTicker = async (ticker) => {
    try {
      const response = await axios.delete(`/api/watchlists/${watchlistId}/items/${ticker}`);
      if (response.data.success) {
        fetchWatchlist();
      } else {
        setError(response.data.error || "Failed to remove ticker");
      }
    } catch (err) {
      setError(err.response?.data?.error || "Failed to remove ticker");
    }
  };

  const handleAnalyzeTicker = async (tickerName) => {
    setAnalyzingTicker(tickerName);
    try {
      const response = await axios.post("/api/analyze", {
        ticker: tickerName,
        analysis_date: new Date().toISOString().split("T")[0],
      });
      if (response.data.success) {
        setSnackbar({ open: true, message: `Analysis queued for ${tickerName}`, severity: "success" });
      } else {
        setSnackbar({ open: true, message: response.data.error || "Failed to start analysis", severity: "error" });
      }
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || "Failed to start analysis", severity: "error" });
    } finally {
      setAnalyzingTicker(null);
    }
  };

  const handleScheduleChange = async (newSchedule) => {
    setUpdatingSchedule(true);
    try {
      const response = await axios.put(`/api/watchlists/${watchlistId}/schedule`, { schedule: newSchedule });
      if (response.data.success) {
        const updated = response.data.data || {};
        setWatchlist(prev => ({
          ...prev,
          schedule: updated.schedule || newSchedule,
          last_scheduled_run: updated.last_scheduled_run || prev.last_scheduled_run,
        }));
        setSnackbar({
          open: true,
          message: newSchedule === 'none' ? 'Scheduled analysis disabled' : `Schedule set to ${newSchedule}`,
          severity: "success",
        });
      } else {
        setSnackbar({ open: true, message: response.data.error || "Failed to update schedule", severity: "error" });
      }
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || "Failed to update schedule", severity: "error" });
    } finally {
      setUpdatingSchedule(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getDecisionChip = (decision) => {
    if (!decision) return <Chip label="No report" size="small" variant="outlined" />;
    const colorMap = {
      BUY: { color: "success" },
      SELL: { color: "error" },
      HOLD: { color: "warning" },
      OUTPERFORM: { sx: { bgcolor: "#81c784", color: "#1b5e20" } },
      UNDERPERFORM: { sx: { bgcolor: "#ef9a9a", color: "#b71c1c" } },
    };
    const config = colorMap[decision] || { color: "default" };
    return (
      <Chip
        label={decision}
        color={config.color}
        size="small"
        sx={{ fontWeight: "bold", ...config.sx }}
      />
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={onBack} sx={{ mr: 1, color: "primary.main" }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" color="primary">
          {watchlist ? watchlist.name : "Watchlist"}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3, backgroundColor: "background.paper", border: "1px solid", borderColor: "divider" }}>
        <Box display="flex" gap={2} alignItems="center">
          <ScheduleIcon sx={{ color: "primary.main" }} />
          <Typography variant="body1" sx={{ color: "text.secondary", minWidth: 120 }}>
            Auto-Analyze:
          </Typography>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              value={watchlist?.schedule || "none"}
              onChange={(e) => handleScheduleChange(e.target.value)}
              disabled={updatingSchedule}
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "primary.main",
                },
              }}
            >
              <MenuItem value="none">None</MenuItem>
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="weekly">Weekly</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
            </Select>
          </FormControl>
          {watchlist?.last_scheduled_run && (
            <Typography variant="body2" sx={{ color: "text.secondary", ml: 1 }}>
              Last run: {formatDate(watchlist.last_scheduled_run)}
            </Typography>
          )}
          {updatingSchedule && <CircularProgress size={20} sx={{ ml: 1 }} />}
        </Box>
      </Paper>

      <Paper sx={{ p: 2, mb: 3, backgroundColor: "background.paper", border: "1px solid", borderColor: "divider" }}>
        <Box display="flex" gap={2} alignItems="center">
          <TextField
            size="small"
            placeholder="Add ticker (e.g. AAPL)..."
            value={newTicker}
            onChange={(e) => setNewTicker(e.target.value.toUpperCase())}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleAddTicker();
            }}
            sx={{
              flexGrow: 1,
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "primary.main",
                },
              },
            }}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddTicker}
            disabled={adding || !newTicker.trim()}
          >
            {adding ? "Adding..." : "Add Ticker"}
          </Button>
        </Box>
      </Paper>

      <TableContainer component={Paper} sx={{ backgroundColor: "background.paper" }}>
        {!watchlist || !watchlist.items || watchlist.items.length === 0 ? (
          <Box display="flex" justifyContent="center" p={4}>
            <Typography color="textSecondary">
              No tickers in this watchlist. Add one above to get started.
            </Typography>
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: "primary.main", fontWeight: "bold" }}>Ticker</TableCell>
                <TableCell sx={{ color: "primary.main", fontWeight: "bold" }}>Last Report</TableCell>
                <TableCell sx={{ color: "primary.main", fontWeight: "bold" }}>Decision</TableCell>
                <TableCell sx={{ color: "primary.main", fontWeight: "bold" }}>Added</TableCell>
                <TableCell sx={{ color: "primary.main", fontWeight: "bold" }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {watchlist.items.map((item) => (
                <TableRow
                  key={item.id}
                  hover
                  sx={{
                    "&:hover": {
                      backgroundColor: "rgba(0, 255, 0, 0.05)",
                    },
                  }}
                >
                  <TableCell>
                    <Link
                      component="button"
                      variant="body1"
                      sx={{ fontWeight: "bold", color: "secondary.main", textDecoration: "none", cursor: "pointer" }}
                      onClick={() => navigate(`/stock/${item.ticker}`)}
                    >
                      {item.ticker}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(item.last_report_time)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {item.last_report_decision ? (
                      item.last_report_job_id ? (
                        <Tooltip title="View Report">
                          <Box
                            sx={{ cursor: "pointer", display: "inline-block" }}
                            onClick={() => navigate(`/reports/${item.last_report_job_id}`)}
                          >
                            {getDecisionChip(item.last_report_decision)}
                          </Box>
                        </Tooltip>
                      ) : (
                        getDecisionChip(item.last_report_decision)
                      )
                    ) : (
                      getDecisionChip(null)
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(item.added_at)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="New Analysis">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleAnalyzeTicker(item.ticker)}
                        disabled={analyzingTicker === item.ticker}
                      >
                        {analyzingTicker === item.ticker ? (
                          <CircularProgress size={18} color="primary" />
                        ) : (
                          <PlayArrowIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                    {item.last_report_job_id && (
                      <Tooltip title="View Report">
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() => navigate(`/reports/${item.last_report_job_id}`)}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Remove from watchlist">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveTicker(item.ticker)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default WatchlistDetail;
